import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Link, usePage } from "@inertiajs/react"

import { help_article_path } from "@/routes"
import type { SharedData } from "@/types"

/**
 * A help article, rendered.
 *
 * This is the one place in the app where long-form reading is the job, and it
 * gets the DESIGN.md prose treatment nothing else uses: headings at the
 * display scale, a readable body, and a measure of about 68 characters
 * because a line of text three times that wide is one the eye loses its place
 * on between lines.
 *
 * Markdown rather than stored HTML, and `react-markdown` rather than
 * `dangerouslySetInnerHTML`: it builds a React tree, and there is deliberately
 * no `rehype-raw`, so an article cannot introduce markup even if somebody
 * pastes a `<script>` into a `.md` file. Same call `assistant-markdown.tsx`
 * made for model output, for the same reason.
 */
const PROSE = [
  "prose max-w-none",
  // Body: a readable size and leading, in ink rather than
  // prose's own greys.
  "text-foreground text-base leading-[1.65]",
  "prose-p:text-foreground prose-p:my-4",
  "prose-li:text-foreground prose-li:my-1 prose-ul:my-4 prose-ol:my-4",
  // Headings: the same face as the chrome, at the sheet title's size. A
  // section heading used to be a step larger than the article's own title
  // above it, which read as the article starting over.
  "prose-headings:text-foreground prose-headings:font-medium",
  "prose-headings:tracking-[-0.01em]",
  "prose-h2:text-xl prose-h2:leading-[1.15] prose-h2:mt-10 prose-h2:mb-3",
  "prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-2",
  "prose-strong:text-foreground prose-strong:font-semibold",
  // Links are underlined ink, not the accent. DESIGN.md allows one action
  // colour per view and on an article that is the button at the end, not
  // fourteen inline links competing with it.
  "prose-a:text-foreground prose-a:underline prose-a:underline-offset-[3px]",
  "prose-a:decoration-border hover:prose-a:decoration-foreground",
  "prose-hr:border-border prose-hr:my-10",
  "prose-blockquote:border-border prose-blockquote:text-muted-foreground",
  // Anything in backticks is a value or an identifier, which DESIGN.md gives
  // to the mono face.
  "prose-code:text-sm prose-code:font-normal",
  "prose-code:before:content-none prose-code:after:content-none",
].join(" ")

export function HelpProse({ body }: { body: string }) {
  // Absent on the portal, which has no account scope and no session. There,
  // `/help/<slug>` is already the correct URL — the desk's own subdomain
  // serves the counterparty corpus at exactly that path (INCO-388) — so the
  // link is left alone rather than rewritten.
  const { current_account: account } = usePage<SharedData>().props as Partial<SharedData>

  return (
    <div className={PROSE}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // An article linking another article should not reload the app.
          // Anything off-site opens in a new tab and does not get to name this
          // window as its opener.
          a: ({ href, children, node: _node, ...props }) => {
            // Articles link each other as `/help/<slug>`, which is what an
            // author can write without knowing which desk is reading. The real
            // route is scoped to the account, so it is resolved here — and the
            // same markdown will resolve to the portal path when these are
            // served on a subdomain.
            const article = href?.match(/^\/help\/([a-z0-9-]+)$/)
            if (article && account) {
              return (
                <Link href={help_article_path(account.uid, article[1])} className={props.className}>
                  {children}
                </Link>
              )
            }

            return href?.startsWith("/") ? (
              <Link href={href} className={props.className}>
                {children}
              </Link>
            ) : (
              <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                {children}
              </a>
            )
          },
        }}
      >
        {body}
      </ReactMarkdown>
    </div>
  )
}
